/**
 * Per-post status after the date in the Schedule Posts
 * inspector (PublicationForm). Compact icons for the same exceptional
 * kinds shown by Calendar tiles and the left shorts list
 * (`publicationStatusPresentation.ts`), plus one Schedule-only mark:
 * "Already scheduled" for posts that entered the session already booked.
 *
 * That mark explains mixed create sessions — persisted scheduled posts
 * beside newly staged drafts — so totals like "Posts 7 of 87" vs
 * "Schedule 81 posts" stay legible. Newly staged posts wear nothing.
 *
 * Posted is a slate-700 check (a fact); Already scheduled stays slate-500.
 * Failure uses red and in-flight work uses blue. Hover, focus and tap explain
 * the status. Post now / Retry now live on the calendar, not here.
 */

import {
  selectPublicationStatusKind,
  type PublicationStatusKind,
} from "./publicationStatusPresentation";

export type PublicationStatusBadgeKind = PublicationStatusKind | "scheduled";

export type PublicationStatusBadge = {
  kind: PublicationStatusBadgeKind;
  label: string;
  /** Color classes for the per-post glyph. Geometry belongs to SchedulePostStatus. */
  className: string;
};

const STATUS_BADGE_TONES: Record<PublicationStatusBadgeKind, string> = {
  scheduled: "text-slate-500 dark:text-foreground-secondary",
  published: "text-slate-700 dark:text-foreground",
  failed: "text-red-600 dark:text-red-400",
  queued: "text-slate-500 dark:text-foreground-secondary",
  publishing: "text-blue-600 dark:text-blue-400",
  verifying: "text-blue-600 dark:text-blue-400",
};

const STATUS_BADGE_LABELS: Record<PublicationStatusBadgeKind, string> = {
  scheduled: "Already scheduled",
  published: "Posted",
  failed: "Failed",
  queued: "Posting soon",
  publishing: "Posting",
  verifying: "Verifying",
};

export const selectPublicationStatusBadge = ({
  status,
  showScheduledBadge,
  statusKind,
}: {
  status: string;
  /** Derived presentation, including a scheduled post waiting to publish. */
  statusKind?: PublicationStatusKind | null;
  /** True only for a persisted booked post (`isSavedPub`). */
  showScheduledBadge: boolean;
}): PublicationStatusBadge | null => {
  // Persisted-vs-staged distinction lives only on this inspector mark —
  // icon surfaces never mark scheduled (see publicationStatusPresentation).
  if (status === "scheduled" && showScheduledBadge && !statusKind) {
    return {
      kind: "scheduled",
      label: STATUS_BADGE_LABELS.scheduled,
      className: STATUS_BADGE_TONES.scheduled,
    };
  }

  const kind = statusKind ?? selectPublicationStatusKind({ status });
  if (!kind) return null;
  return {
    kind,
    label: STATUS_BADGE_LABELS[kind],
    className: STATUS_BADGE_TONES[kind],
  };
};
